import { useContext, useEffect, useState } from 'react'
import firebase from '../../firebase/compat'
import { db } from '../../firebase/config'
import { client } from '../../hooks/Client'
import { Auth } from '../../StateManagment/Auth'
import { useFirestoreGeneral } from '../../firebase/useFirestore'
import useSettings from '../../hooks/Settings'
import usePersonaTerms from '../../hooks/usePersonaTerms'
import ReadOnlyGuard from '../common/ReadOnlyGuard'
import buildOutputEventResult from '../../helpers/personas/buildOutputEventResult'
import scheduleOutputDocId from '../../helpers/personas/scheduleOutputDocId'
import amsterdamDateKey from '../../helpers/personas/amsterdamDateKey'
import pickLowestPosition from '../../helpers/personas/pickLowestPosition'
import notDeleted from '../../helpers/notDeleted'
import { v4 as uuid } from 'uuid'

/**
 * Output registreren vanuit de agenda, bij de roosterregel van een deelnemer.
 *
 * Schrijft naar hetzelfde document als de nachtklus (zelfde vaste ID per
 * roosterregel per dag), zodat dezelfde dag nooit dubbel geteld wordt: wie het
 * eerst is legt vast, de ander ziet dat het er al staat.
 *
 * @param {object} props
 * @param {string} props.scheduleId - Document-ID van de roosterregel
 * @param {string} props.personaId - De deelnemer van deze roosterregel
 * @param {string} props.outputId - De hoofd-output van de roosterregel
 * @param {Date} props.taskDate - De dag waarop dit item in de agenda staat
 * @param {() => void} [props.onSaved] - Aangeroepen na een geslaagde registratie
 * @returns {JSX.Element}
 */
const ScheduleOutputRegistration = ({ scheduleId, personaId, outputId, taskDate, onSaved }) => {
    const [auth] = useContext(Auth)
    const personaTerms = usePersonaTerms()
    const personasOn = useSettings().personasOn

    const [amount, setAmount] = useState(1)
    const [participants, setParticipants] = useState([])
    const [existing, setExisting] = useState(null)
    const [loading, setLoading] = useState(true)
    const [message, setMessage] = useState('')

    const dateKey = amsterdamDateKey(taskDate)
    const docId = scheduleOutputDocId(scheduleId, dateKey)

    const outputs = useFirestoreGeneral('Outputs', 'ID', outputId || '')
    const output = outputs && outputs.length > 0 ? outputs[0] : null
    const activePersonas = useFirestoreGeneral('Personas', 'Status', 'active')

    // Wat er al staat ophalen, zodat de gebruiker ziet of de nachtklus of een
    // collega deze dag al heeft vastgelegd.
    useEffect(() => {
        if (!docId) { setLoading(false); return undefined }

        let cancelled = false
        setLoading(true)

        db.collection('EventResults').doc(docId).get()
            .then((snap) => {
                if (cancelled) return
                // Een registratie in de prullenbak bestaat voor de gebruiker
                // niet; opnieuw registreren overschrijft het document en haalt
                // de verwijdermarkering weg.
                const data = snap.exists && notDeleted(snap.data()) ? snap.data() : null
                setExisting(data)
                if (data) {
                    setAmount(Number(data.Value) || 1)
                    // Ontbreekt het veld, dan komt de registratie van vóór de
                    // omzetting naar Participants; de ingeroosterde deelnemer is
                    // dan de juiste beginstand. Een lege lijst laten we staan:
                    // die heeft iemand bewust zo opgeslagen.
                    setParticipants(
                        Array.isArray(data.Participants)
                            ? data.Participants
                            : (personaId ? [personaId] : [])
                    )
                } else {
                    setParticipants(personaId ? [personaId] : [])
                }
                setLoading(false)
            })
            .catch((err) => {
                if (cancelled) return
                console.error('Kon de bestaande registratie niet ophalen:', err)
                setMessage('De bestaande registratie kon niet worden opgehaald.')
                setLoading(false)
            })

        return () => { cancelled = true }
    }, [docId, personaId])

    const toggleParticipant = (id) => {
        setParticipants((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
    }

    const save = async () => {
        if (!docId || !output) return
        setMessage('')

        try {
            const activityId = output.ActivityID || ''

            // Formulier en outputs-veld opzoeken; bij meerdere wint altijd
            // hetzelfde, net als in de nachtklus.
            const formsSnap = await db.collection('EventForms')
                .where('CompagnyID', '==', client)
                .where('ActivityID', '==', activityId)
                .get()

            const eventFormId = pickLowestPosition(formsSnap.docs)?.ID || ''
            if (!eventFormId) {
                setMessage('Deze activiteit heeft nog geen registratieformulier.')
                return
            }

            const fieldsSnap = await db.collection('EventFields')
                .where('EventId', '==', eventFormId)
                .where('Type', '==', 'outputs')
                .get()

            const doc = buildOutputEventResult({
                id: existing?.ID || uuid(),
                sessionId: existing?.SessionID || uuid(),
                compagnyId: output.CompagnyID || client,
                outputID: outputId,
                activityId,
                eventFormId,
                fieldId: pickLowestPosition(fieldsSnap.docs)?.ID || '',
                timestamp: firebase.firestore.Timestamp.fromDate(taskDate),
                value: Number(amount) || 0,
                position: existing?.Position || 1,
                participants,
                personaId,
                user: auth?.ID || '',
                scheduleID: scheduleId,
                dateKey,
            })

            await db.collection('EventResults').doc(docId).set(doc)

            setExisting(doc)
            setMessage('Opgeslagen.')
            onSaved && onSaved()
        } catch (err) {
            console.error('Opslaan van de outputregistratie mislukt:', err)
            setMessage('Opslaan is mislukt. Probeer het opnieuw.')
        }
    }

    if (!outputId) {
        return (
            <p className="schedule-output-empty">
                Deze roosterregel heeft nog geen hoofd-output. Kies er een bij het rooster van
                de {personaTerms.singularLower} om hier te kunnen registreren.
            </p>
        )
    }

    if (loading) return <p className="schedule-output-empty">Gegevens laden…</p>

    return (
        <ReadOnlyGuard edit="personas.edit">
            {(canEdit) => (
                <div className="schedule-output-registration">
                    <p className="schedule-output-title"><b>{output?.Title || 'Output'}</b></p>

                    {existing && (
                        <p className="schedule-output-existing">
                            Voor deze dag is al {existing.Value} geregistreerd
                            {existing.User === 'Deccos' ? ' (automatisch)' : ''}. Aanpassen mag.
                        </p>
                    )}

                    <label className="schedule-output-amount">
                        <span>Aantal</span>
                        <input
                            type="number"
                            min="0"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                        />
                    </label>

                    {personasOn === 'flex' && (
                        <div className="schedule-output-participants">
                            <p><b>Wie waren aanwezig?</b></p>
                            {(activePersonas || []).map((persona) => (
                                <label key={persona.ID} className="schedule-output-participant">
                                    <input
                                        type="checkbox"
                                        checked={participants.includes(persona.ID)}
                                        onChange={() => toggleParticipant(persona.ID)}
                                    />
                                    <span>{persona.Name}</span>
                                </label>
                            ))}
                        </div>
                    )}

                    {canEdit && (
                        <button type="button" className="schedule-output-save" onClick={save}>
                            {existing ? 'Bijwerken' : 'Registreren'}
                        </button>
                    )}

                    {message && <p className="schedule-output-message" role="status">{message}</p>}
                </div>
            )}
        </ReadOnlyGuard>
    )
}

export default ScheduleOutputRegistration
